// src/components/common/Sidebar/Sidebar.tsx

import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styles from './Sidebar.module.css';
import { menuApi } from '@/api/menu/api';
import { MenuItem } from '@/api/menu/type';

interface SidebarProps {
    isOpen: boolean;
    onMenuClick?: () => void;
}

const Sidebar = ({ isOpen, onMenuClick }: SidebarProps) => {
    const location = useLocation();
    const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [isMenuLoading, setIsMenuLoading] = useState(true);

    // ─── 메뉴 API 호출 ────────────────────────────────────────────
    useEffect(() => {
        const fetchMenus = async () => {
            try {
                setIsMenuLoading(true);
                const response = await menuApi.getMenuList();
                if (response.code === 0) {
                    setMenuItems(response.data);
                } else {
                    console.error('메뉴 로드 실패:', response.message);
                }
            } catch (error) {
                console.error('메뉴 API 오류:', error);
            } finally {
                setIsMenuLoading(false);
            }
        };

        fetchMenus();
    }, []); // 마운트 시 1회 호출 (토큰은 apiClient 인터셉터가 자동 첨부)

    // ─── 경로 변경 시 활성 메뉴 갱신 ─────────────────────────────
    useEffect(() => {
        if (menuItems.length === 0) return;
        const activeMenu = findActiveSubmenu(location.pathname);
        setActiveSubmenu(activeMenu);
    }, [location.pathname, menuItems]);

    // ─── 경로 매칭 헬퍼 ──────────────────────────────────────────
    const isPathMatch = (menuPath: string, currentPath: string): boolean => {
        if (menuPath === currentPath) return true;
        if (currentPath.startsWith(menuPath + '/')) return true;
        return false;
    };

    const findActiveSubmenu = (pathname: string): string | null => {
        for (const item of menuItems) {
            if (item.children) {
                const hasActiveChild = item.children.some(
                    child => child.path && isPathMatch(child.path, pathname),
                );
                if (hasActiveChild) return item.id;
            }
        }
        return null;
    };

    const toggleSubmenu = (menuId: string) => {
        setActiveSubmenu(prev => (prev === menuId ? null : menuId));
    };

    const handleMenuClick = () => {
        onMenuClick?.();
    };

    // ─── 공통 서브 컴포넌트 ───────────────────────────────────────
    const MenuBadge = ({ badge }: { badge: string }) => (
        <span className="badge bg-warning-transparent ms-2">{badge}</span>
    );

    const MenuLabel = ({ title }: { title: string }) => (
        <span className="side-menu__label">{title}</span>
    );

    const MenuIcon = ({ icon }: { icon: string }) => <i className={icon}></i>;

    const getMenuItemClasses = (isActive: boolean) => ({
        menuItem: `side-menu__item ${isActive ? 'active' : ''}`,
        submenu: `slide-menu child1 ${isActive ? 'active' : ''} ${isActive ? styles.open : ''}`,
    });

    // ─── 메뉴 렌더링 ─────────────────────────────────────────────
    const renderMenuItem = (item: MenuItem, level: number = 0) => {
        const { id, title, icon, path, children: rawChildren, badge } = item;
        const children = rawChildren ?? [];
        const hasChildren = children.length > 0;
        const isSubmenuActive = activeSubmenu === id;
        const classes = getMenuItemClasses(isSubmenuActive);

        if (!hasChildren) {
            const isUnderDevelopment = !path || path === '/dev';
            const isExternalLink = path?.startsWith('http');

            // 외부 링크면 <a target="_blank"> 로 렌더링
            if (isExternalLink) {
                return (
                    <li key={`${id}-${path}`} className="slide">
                        <a
                            href={path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="side-menu__item"
                            onClick={handleMenuClick}
                        >
                            {icon && <MenuIcon icon={icon} />}
                            <MenuLabel title={title} />
                            {badge && <MenuBadge badge={badge} />}
                            <i className="ri-external-link-line ms-auto fs-12 opacity-50" />
                        </a>
                    </li>
                );
            }

            return (
                <li key={`${id}-${path}`} className="slide">
                    <NavLink
                        to={path || '#'}
                        className={() => {
                            const customActive =
                                path && isPathMatch(path, location.pathname);
                            return `side-menu__item ${customActive ? 'active' : ''}`;
                        }}
                        onClick={e => {
                            if (isUnderDevelopment) {
                                e.preventDefault();
                                alert('개발중입니다.');
                                return;
                            }
                            handleMenuClick();
                        }}
                    >
                        {icon && <MenuIcon icon={icon} />}
                        <MenuLabel title={title} />
                        {badge && <MenuBadge badge={badge} />}
                    </NavLink>
                </li>
            );
        }

        return (
            <li
                key={id}
                className={`slide has-sub ${isSubmenuActive ? 'open' : ''}`}
            >
                <a
                    href="#"
                    className={classes.menuItem}
                    onClick={e => {
                        e.preventDefault();
                        toggleSubmenu(id);
                    }}
                >
                    <MenuIcon icon={icon} />
                    <MenuLabel title={title} />
                    {badge && <MenuBadge badge={badge} />}
                    <i className="fe fe-chevron-right side-menu__angle" />
                </a>
                <ul className={classes.submenu}>
                    {children.map(child => renderMenuItem(child, level + 1))}
                </ul>
            </li>
        );
    };

    // ─── 로딩 스켈레톤 ────────────────────────────────────────────
    const MenuSkeleton = () => (
        <>
            {Array.from({ length: 6 }).map((_, i) => (
                <li key={i} className="slide">
                    <div
                        style={{
                            height: '40px',
                            margin: '4px 12px',
                            borderRadius: '6px',
                            background: 'rgba(255,255,255,0.06)',
                            animation: 'pulse 1.5s ease-in-out infinite',
                        }}
                    />
                </li>
            ))}
        </>
    );

    // ─── 렌더 ─────────────────────────────────────────────────────
    return (
        <aside
            className={`app-sidebar sticky ${isOpen ? 'open' : ''}`}
            id="sidebar"
        >
            <div className="main-sidebar-header">
                <a href="/" className="header-logo">
                    <img
                        src="/images/common/logo/logo_dark.png"
                        alt="logo"
                        className="desktop-logo"
                    />
                    <img
                        src="/images/common/logo/logo_short_dark.png"
                        alt="logo"
                        className="toggle-logo"
                    />
                    <img
                        src="/images/common/logo/logo_lite.png"
                        alt="logo"
                        className="desktop-white"
                    />
                    <img
                        src="/images/common/logo/logo_short_lite.png"
                        alt="logo"
                        className="toggle-white"
                    />
                </a>
            </div>

            <div className="main-sidebar" id="sidebar-scroll">
                <nav className="main-menu-container nav nav-pills flex-column sub-open">
                    <div className="slide-left" id="slide-left">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="#7b8191"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                        >
                            <path d="M13.293 6.293 7.586 12l5.707 5.707 1.414-1.414L10.414 12l4.293-4.293z"></path>
                        </svg>
                    </div>

                    <ul className="main-menu">
                        <li className="slide__category">
                            <span className="category-name">Main</span>
                        </li>

                        {isMenuLoading ? (
                            <MenuSkeleton />
                        ) : (
                            menuItems.map(item => renderMenuItem(item))
                        )}
                    </ul>

                    <div className="slide-right" id="slide-right">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="#7b8191"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                        >
                            <path d="m10.707 17.707 5.707-5.707-5.707-5.707-1.414 1.414L13.586 12l-4.293 4.293z"></path>
                        </svg>
                    </div>
                </nav>
            </div>
        </aside>
    );
};

export default Sidebar;
